import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Calculator, FileText, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const brDate = (s) => s ? new Date((s + "").slice(0,10) + "T00:00:00").toLocaleDateString("pt-BR") : "—";
const brl = (c) => (Number(c || 0)/100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PreparadoHermes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/hermes/prepared", { params: { days: 30 } });
        setData(r.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-6xl mx-auto w-full flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin"/> Carregando…
    </div>
  );
  if (!data) return null;

  const { counts, calculations, impugnations, awaiting_calc, review_deadlines } = data;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="prepared-page">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center text-white shadow-lg shadow-amber-500/30">
          <Sparkles className="w-7 h-7"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
            PREPARADO PELO HERMES
          </div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">
            {counts.total > 0
              ? `${counts.total} entregas prontas para sua revisão`
              : "Ainda sem trabalho preparado nos últimos 30 dias"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Cálculos gerados, minutas de impugnação, casos aguardando cálculo
            e prazos em revisão — tudo o que o Hermes deixou pronto para você decidir.
          </p>
        </div>
      </div>

      {/* Cálculos prontos */}
      <Section title="Cálculos prontos" icon={Calculator} color="emerald"
                count={counts.calculations} testid="prepared-calcs">
        {calculations.map(c => (
          <Row key={c.id}
            testid={`prepared-calc-${c.id}`}
            title={c.process_number || "Processo sem número"}
            subtitle={
              (c.claimant ? `Reclamante: ${c.claimant}` : "") +
              (c.defendant ? ` · Reclamada: ${c.defendant}` : "") +
              ` · ${brDate(c.generated_at)}`
            }
            right={
              <span className="font-mono font-semibold text-emerald-800">
                {brl(c.total_updated_cents || c.total_liquido_cents)}
              </span>
            }
            actions={[
              { href: `${API}/liq/calculations/${c.id}/pdf`, label: "PDF" },
              { href: `${API}/liq/calculations/${c.id}/export/pjc`, label: ".pjc" },
              c.public_token && { href: `/verificacao/calculo/${c.public_token}`, label: "Selo", target: "_blank" },
            ].filter(Boolean)}
          />
        ))}
      </Section>

      {/* Impugnações prontas */}
      <Section title="Impugnações prontas" icon={FileText} color="rose"
                count={counts.impugnations} testid="prepared-impugs">
        {impugnations.map(i => (
          <Row key={i.id}
            testid={`prepared-impug-${i.id}`}
            title={i.process_number || "Impugnação"}
            subtitle={
              (i.claimant ? `Reclamante: ${i.claimant}` : "") +
              ` · Δ ${i.delta_pct.toFixed(1)}%` +
              ` · ${brDate(i.generated_at)}`
            }
            right={<span className="font-mono text-rose-700 text-xs">R$ {(i.delta_cents/100).toLocaleString("pt-BR")}</span>}
            actions={[
              { href: `${API}/liq/impugnacoes/${i.id}/docx`, label: ".docx" },
            ]}
          />
        ))}
      </Section>

      {/* Casos aguardando cálculo */}
      <Section title="Casos aguardando cálculo" icon={Clock} color="amber"
                count={counts.awaiting_calc} testid="prepared-awaiting">
        {awaiting_calc.map(c => (
          <Row key={c.id}
            testid={`prepared-await-${c.id}`}
            title={c.process_number || "Processo"}
            subtitle={
              ((c.claimant || {}).name ? `Reclamante: ${(c.claimant || {}).name}` : "") +
              ` · Criado ${brDate(c.created_at)}`
            }
            right={<span className="text-xs text-amber-700 font-semibold">TÍTULO CONSOLIDADO</span>}
            actions={[{ href: `/calculos/${c.id}/calculo`, label: "Calcular" }]}
          />
        ))}
      </Section>

      {/* Prazos em revisão */}
      <Section title="Prazos em revisão" icon={AlertTriangle} color="sky"
                count={counts.review_deadlines} testid="prepared-review-deadlines">
        {review_deadlines.map(d => (
          <Row key={d.id}
            testid={`prepared-review-${d.id}`}
            title={d.description || d.kind || "Prazo"}
            subtitle={`Processo ${d.process_number || "—"} · Detectado ${brDate(d.created_at)}`}
            right={<span className="font-mono text-sky-800 font-semibold">venc. {brDate(d.due_date)}</span>}
            actions={d.process_number ? [{ href: `/processos/${encodeURIComponent(d.process_number)}`, label: "Abrir" }] : []}
          />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, color, count, children, testid }) {
  if (!count) return null;
  const cls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    sky:     "border-sky-200 bg-sky-50 text-sky-800",
  }[color];
  return (
    <div className="mb-6" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 rounded-lg px-3 py-2 border ${cls}`}>
        <Icon className="w-4 h-4"/>
        <h2 className="font-semibold text-sm">{title}</h2>
        <span className="ml-auto text-xs font-mono font-bold">{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ title, subtitle, right, actions = [], testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3"
          data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 font-medium truncate">{title}</div>
        <div className="text-xs text-slate-500 truncate">{subtitle}</div>
      </div>
      {right}
      <div className="flex items-center gap-1 shrink-0">
        {actions.map((a, i) => (
          <a key={i} href={a.href} target={a.target || "_self"}
             className="text-xs h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1">
            {a.label}
          </a>
        ))}
      </div>
    </div>
  );
}
